import React, { useState } from 'react';
import { Save, Globe, Shield, Bell, Database, Palette } from 'lucide-react';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    siteName: 'EPIST Admin Portal',
    siteDescription: 'Administrative interface for EPIST system',
    timezone: 'UTC',
    language: 'en',
    emailNotifications: true,
    pushNotifications: false,
    maintenanceMode: false,
    debugMode: false,
    theme: 'light',
  });

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    console.log('Saving settings:', settings);
    // Add save logic here
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'system', label: 'System', icon: Database },
    { id: 'appearance', label: 'Appearance', icon: Palette },
  ];

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h3>General Settings</h3>
      <div className="settings-form">
        <div className="form-group">
          <label htmlFor="siteName">Site Name</label>
          <input
            type="text"
            id="siteName"
            value={settings.siteName}
            onChange={(e) => handleSettingChange('siteName', e.target.value)}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="siteDescription">Site Description</label>
          <textarea
            id="siteDescription"
            value={settings.siteDescription}
            onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
            className="form-textarea"
            rows="3"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="timezone">Timezone</label>
            <select
              id="timezone"
              value={settings.timezone}
              onChange={(e) => handleSettingChange('timezone', e.target.value)}
              className="form-select"
            >
              <option value="UTC">UTC</option>
              <option value="EST">Eastern Time</option>
              <option value="PST">Pacific Time</option>
              <option value="GMT">GMT</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="language">Language</label>
            <select
              id="language"
              value={settings.language}
              onChange={(e) => handleSettingChange('language', e.target.value)}
              className="form-select"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <div className="settings-form">
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkmark"></span>
            Enable Maintenance Mode
          </label>
          <p className="setting-description">
            When enabled, only administrators can access the system
          </p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.debugMode}
              onChange={(e) => handleSettingChange('debugMode', e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkmark"></span>
            Debug Mode
          </label>
          <p className="setting-description">
            Enable detailed error logging and debugging information
          </p>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Settings</h3>
      <div className="settings-form">
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.emailNotifications}
              onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkmark"></span>
            Email Notifications
          </label>
          <p className="setting-description">Receive important updates via email</p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.pushNotifications}
              onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkmark"></span>
            Push Notifications
          </label>
          <p className="setting-description">Receive real-time notifications in the browser</p>
        </div>
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="settings-section">
      <h3>System Settings</h3>
      <div className="settings-form">
        <div className="form-group">
          <label>Database Status</label>
          <div className="status-indicator">
            <span className="status-dot status-online"></span>
            <span>Online</span>
          </div>
        </div>

        <div className="form-group">
          <label>Last Backup</label>
          <div className="backup-info">
            <span>2024-01-15 14:30:00 UTC</span>
            <button className="btn btn-secondary">Backup Now</button>
          </div>
        </div>

        <div className="form-group">
          <label>System Version</label>
          <span className="version-info">v1.2.3</span>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="settings-section">
      <h3>Appearance Settings</h3>
      <div className="settings-form">
        <div className="form-group">
          <label>Theme</label>
          <div className="theme-options">
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={settings.theme === 'light'}
                onChange={(e) => handleSettingChange('theme', e.target.value)}
              />
              <div className="theme-preview theme-light">
                <span>Light</span>
              </div>
            </label>

            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={settings.theme === 'dark'}
                onChange={(e) => handleSettingChange('theme', e.target.value)}
              />
              <div className="theme-preview theme-dark">
                <span>Dark</span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'system':
        return renderSystemSettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2>Settings</h2>
        <p>Configure your system preferences and options</p>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="settings-content">
          {renderContent()}

          <div className="settings-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
